import { DocumentType } from "@src/__generated__/graphql";
import { DocumentManagerRoot } from "@src/components/modules/DocumentManager/DocumentManagerRoot";
import { DocumentSearch } from "@src/components/modules/DocumentManager/DocumentSearch";
import { useDocumentManager } from "@src/components/modules/DocumentManager/useDocumentManager";
import { useDocumentUpload } from "@src/components/modules/Upload/useDocumentUpload";
import React, { useCallback, useState } from "react";
import styled from "styled-components/macro";

export const StyledRoot = styled.div<{ error: boolean }>`
  @keyframes flashin {
    from {
      background-color: var(--highlight-green);
    }
    to {
      background-color: var(--light-white);
    }
  }

  border: 1px solid
    ${({ error }) => (error ? "var(--error-color)" : "var(--page-background)")};
  border-radius: var(--rounded-corner);
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: auto;
  position: relative;

  .font-small {
    font-size: var(--font-size-small);
  }

  .drop-area {
    visibility: hidden;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    background-color: transparent;

    &.hover {
      visibility: visible;
      z-index: 5;
      background-color: rgba(var(--highlight-green-rgb), 0.1);
    }

    .spinner {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 4rem;
      height: auto;
    }
  }

  .document-preview {
    width: 50vw;
    height: 50vh;
  }

  .attached-docs {
    flex-shrink: 0;
    position: sticky;
    top: 0;
    z-index: 2;
    padding: 1rem;
    max-height: 30vh;
    overflow-y: auto;
    background: var(--page-background);
    box-shadow: var(--drop-shadow);
    border-radius: var(--rounded-corner-large);
    border: 1px solid var(--border-gray-light);

    .label {
      overflow-wrap: anywhere;
    }

    .attached {
      animation-name: flashin;
      animation-duration: 0.3s;
      animation-iteration-count: 1;
    }
  }

  .file-input-label {
    font-family: var(--inter-font);
    font-size: var(--font-size-xxsmall);
    font-weight: var(--font-weight-bold);
    padding: 0.75rem 1rem;
    border-radius: var(--rounded-corner);
    border: 1px solid var(--border-gray);
    background: var(--white);
    box-shadow: var(--drop-shadow-light);
    color: var(--primary-black);
    transition: var(--transition);
    cursor: pointer;
    &:hover {
      background: var(--page-background);
      box-shadow: var(--drop-shadow);
    }
  }

  .box {
    .view-icon,
    .label {
      overflow-wrap: anywhere;
      cursor: pointer;
    }

    .label {
      flex-grow: 1;
    }

    .view-icon {
      flex-shrink: 0;
      width: 1.25rem;
      height: 1.25rem;
      fill: var(--gray);
    }

    &:hover {
      background: var(--light-blue);
    }
  }

  .closeButton {
    align-self: flex-end;
    margin-top: 1rem;
    margin-right: 1rem;
  }
`;

export type DocumentForDocumentManager = {
  displayName: string;
  id: string;
};

export type DocumentManagerProps = {
  existingDocs: DocumentForDocumentManager[];
  onDocsChanged?: (docs: DocumentForDocumentManager[]) => void;
  onDocsAdded?: (docs: DocumentForDocumentManager[]) => void;
  onDocRemoved?: (doc: DocumentForDocumentManager) => void;
  onClose?: () => void;
  vendorId?: string;
  expanded?: boolean;
  bare?: boolean;
  showCloseButton?: boolean;
  className?: string;
  error?: string;
  documentManagerRef?: React.Ref<HTMLDivElement>;
  forceDocumentTypes?: `${DocumentType}`[];
};

export const DocumentManager: React.FC<DocumentManagerProps> = ({
  error,
  existingDocs,
  onDocsChanged,
  onDocsAdded: externalOnDocsAdded,
  onDocRemoved,
  onClose,
  expanded = false,
  bare = false,
  showCloseButton = true,
  className,
  documentManagerRef,
  forceDocumentTypes,
}) => {
  const { attachedDocs, addDocs, removeDoc, fileChangeTriggerRef } =
    useDocumentManager({
      existingDocs,
      onDocsChanged,
      onDocRemoved,
      onDocsAdded: externalOnDocsAdded,
    });

  const { addFiles, loading } = useDocumentUpload();

  const [activeDocument, setActiveDocument] = useState<{
    displayName: string;
    id: string;
  } | null>(null);

  const onDocsAdded = useCallback(
    async (docs: File[]) => {
      const newDocs = await addFiles({ files: docs, forceDocumentTypes });
      addDocs(
        newDocs.map((d) => ({
          ...d.createDocument,
          selected: true,
        })),
      );
      externalOnDocsAdded?.(newDocs.map((d) => d.createDocument));
    },
    [addFiles, addDocs, externalOnDocsAdded, forceDocumentTypes],
  );

  return (
    <DocumentManagerRoot
      documentManagerRef={documentManagerRef}
      error={error}
      className={error ? `field-error ${className ?? ""}` : className ?? ""}
      onDocsAdded={onDocsAdded}
      loading={loading}
      bare={bare}
      onClose={onClose}
      attachedDocs={attachedDocs}
      onRemoveDoc={removeDoc}
      onDocumentPreviewClose={() => setActiveDocument(null)}
      onDocumentSelected={(doc) => {
        setActiveDocument({
          displayName: doc.displayName,
          id: doc.id,
        });
      }}
      activeDocument={activeDocument ?? undefined}
      ref={fileChangeTriggerRef}
      showCloseButton={showCloseButton}
    >
      <DocumentSearch
        expanded={expanded}
        onDocumentClick={setActiveDocument}
        onSearchResultDeselected={removeDoc}
        onSearchResultSelected={(doc) => {
          addDocs([doc], false);
          externalOnDocsAdded?.([doc]);
        }}
        selectedDocumentIds={attachedDocs.map((d) => d.id)}
      />
    </DocumentManagerRoot>
  );
};
